import type { FeatureContent } from "@/lib/features/content";

export const PT_BR_FEATURES: Partial<Record<string, FeatureContent>> = {
  "auto-clip": {
    related: [
      "/use-cases/clippers",
      "/use-cases/podcast-clipping",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-opus-clip",
      "/compare/crayo-vs-vizard",
      "/compare/crayo-vs-klap",
    ],
    title: "Auto Clip",
    answer:
      "O Auto Clip pega um vídeo longo que você envia, como um podcast, uma live ou um webinar, e usa IA para encontrar e cortar os melhores momentos em clipes curtos com legendas. Foi feito para clipadores, podcasters, streamers e agências que transformam gravações longas em vídeos curtos em grande volume.",
    description:
      "Envie um vídeo longo e deixe a IA encontrar os melhores momentos e cortar em clipes prontos para postar.",
    heroPrefix: "Transforme vídeos longos em",
    heroHighlight: "clipes virais",
    heroSuffix: "automaticamente",
    heroImage: "/features/auto-clip/auto-clip-hero.png",
    buttonText: "Clipar um vídeo agora",
    buttonLink: "/create/auto-clip",
    seoTitle: "Cortar clipes com IA: Auto Clipper do Crayo",
    seoDescription:
      "Envie podcasts, lives ou qualquer vídeo longo e deixe a IA cortar os melhores momentos em clipes curtos virais em segundos.",
    faqs: [
      {
        question: "Como o Auto Clip escolhe os momentos?",
        answer:
          "A IA analisa o vídeo inteiro em busca de ganchos, como histórias, punchlines e trechos de alta energia, e dá uma nota para cada clipe para você postar os mais fortes primeiro.",
      },
      {
        question: "Qual o tamanho máximo do meu vídeo?",
        answer:
          "Conteúdo longo é justamente a ideia: podcasts, lives e webinars funcionam muito bem. Vídeos mais longos só levam um pouco mais de tempo para processar.",
      },
      {
        question: "Os clipes vêm com legendas?",
        answer:
          "Sim. Cada clipe recebe legendas precisas e estilizadas automaticamente, e você pode ajustar o visual antes de exportar.",
      },
      {
        question: "Posso editar os clipes antes de postar?",
        answer:
          "Sim. Abra qualquer clipe no editor para cortar, reenquadrar ou mudar o estilo das legendas antes de exportar.",
      },
    ],
  },
  "ai-captions": {
    related: [
      "/features/auto-clip",
      "/features/video-editor",
      "/features/clip-cutter",
      "/use-cases/podcast-clipping",
      "/compare/crayo-vs-submagic",
      "/compare/crayo-vs-captions-ai",
    ],
    title: "AI Captions",
    answer:
      "O AI Captions transcreve a fala de um clipe e transforma em legendas animadas sincronizadas palavra por palavra, com 32 modelos de legenda e transcrição em 56 idiomas falados. É para clipadores, podcasters e streamers que querem cada short legendado para quem assiste sem som, sem digitar uma palavra.",
    description:
      "Legendas precisas e animadas em cada clipe. Escolha um modelo, destaque palavras-chave e ajuste o estilo no editor.",
    heroPrefix: "Adicione",
    heroHighlight: "legendas com IA",
    heroSuffix: "em cada clipe automaticamente",
    heroImage: "/features/ai-captions/ai-captions-hero.png",
    buttonText: "Legendar um clipe agora",
    buttonLink: "/create/auto-clip",
    seoTitle: "Legendas automáticas com IA para Shorts, Reels e TikTok",
    seoDescription:
      "Legendas animadas para Shorts, Reels e TikTok com IA. 32 modelos de legenda, 56 idiomas falados, palavras-chave em destaque e controle total do estilo.",
    faqs: [
      {
        question: "Onde as legendas são adicionadas?",
        answer:
          "O Auto Clip e o editor de vídeo com IA legendam cada clipe que produzem. No editor de linha do tempo você também pode gerar legendas com IA em qualquer vídeo, ou escrever legendas manuais linha por linha.",
      },
      {
        question: "Quais idiomas o Crayo consegue transcrever?",
        answer:
          "56 idiomas falados, incluindo inglês, espanhol, português, francês, alemão, hindi, árabe, japonês e coreano. Você escolhe o idioma falado antes de iniciar o trabalho para que a transcrição use o modelo certo.",
      },
      {
        question: "Posso mudar a aparência das legendas?",
        answer:
          "Sim. Escolha um dos 32 modelos e depois ajuste palavras por linha, posição, cor do texto, contorno, sombra, animação e ênfase. O destaque de palavras-chave realça as palavras importantes, e as mudanças podem valer para uma linha ou para todas.",
      },
      {
        question: "Posso corrigir uma palavra que a transcrição errou?",
        answer:
          "Sim. Abra o clipe no editor, selecione a linha da legenda e edite o texto. A sincronização e o estilo continuam iguais.",
      },
    ],
  },
  "find-viral-moments": {
    related: [
      "/features/auto-clip",
      "/features/clip-cutter",
      "/features/ai-captions",
      "/use-cases/clippers",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-opus-clip",
    ],
    title: "Find Viral Moments",
    answer:
      "O Find Viral Moments analisa um podcast, live ou entrevista de até 3 horas, dá nota aos momentos com mais chance de viralizar e salva cada um como um clipe próprio. É para clipadores e equipes de podcast que querem primeiro a lista dos melhores trechos e a edição depois.",
    description:
      "Envie um vídeo longo, conte o que tem nele e receba um conjunto de momentos ranqueados que você pode revisar, cortar e salvar.",
    heroPrefix: "Encontre os",
    heroHighlight: "momentos virais",
    heroSuffix: "de qualquer vídeo longo",
    heroImage: "/features/find-viral-moments/find-viral-moments-hero.png",
    buttonText: "Encontrar momentos agora",
    buttonLink: "/create/find-moments",
    seoTitle: "Encontre momentos virais em vídeos longos com IA",
    seoDescription:
      "Envie um podcast, live ou entrevista e deixe a IA encontrar e ranquear os momentos que valem um post. Revise, corte e salve até 50 clipes de um só vídeo.",
    faqs: [
      {
        question: "O que eu controlo antes de a busca começar?",
        answer:
          "Quantos momentos buscar (de 5 a 50), a duração de cada momento (30, 45, 60 ou 90 segundos), o idioma falado, o tipo de conteúdo (conversa, podcast, entrevista, ação ou detecção automática) e uma nota de foco opcional, como só as partes sobre preços.",
      },
      {
        question: "Posso revisar os momentos antes de salvar?",
        answer:
          "Sim. Ative Revisar antes de salvar e os momentos abrem no Clip Cutter, onde você mantém, pula ou corta cada um antes de virar clipe. Se deixar desativado, cada momento é salvo nos seus Assets assim que a busca termina.",
      },
      {
        question: "Qual o tamanho máximo do vídeo de origem?",
        answer:
          "De 1 minuto a 3 horas. Vídeos mais longos só levam mais tempo para analisar.",
      },
      {
        question: "Quanto custa em créditos?",
        answer:
          "Um crédito de workflow por momento encontrado. Os momentos que não encontramos são devolvidos, e você pode rodar até três buscas ao mesmo tempo.",
      },
    ],
  },
  "ai-reframe": {
    related: [
      "/features/auto-clip",
      "/features/video-crop",
      "/features/video-editor",
      "/use-cases/podcast-clipping",
      "/compare/crayo-vs-opus-clip",
      "/compare/crayo-vs-vizard",
    ],
    title: "AI Reframe",
    answer:
      "O AI Reframe transforma uma gravação 16:9 em um clipe 9:16 detectando rostos, seguindo quem está falando e movendo o enquadramento com movimentos de câmera suaves e firmes em vez de um rastreio tremido. É para clipadores, podcasters e streamers que precisam de clipes verticais com quem fala sempre no quadro.",
    description:
      "A detecção de rostos e o rastreio de quem fala mantêm a pessoa centralizada enquanto o enquadramento passa de horizontal para vertical.",
    heroPrefix: "Reenquadre de",
    heroHighlight: "16:9 para 9:16",
    heroSuffix: "com rastreio de rosto",
    heroImage: "/features/ai-reframe/ai-reframe-hero.png",
    buttonText: "Reenquadrar um vídeo agora",
    buttonLink: "/create/auto-clip",
    seoTitle: "AI Reframe: de 16:9 para 9:16 com rastreio de rosto",
    seoDescription:
      "Converta podcasts e lives horizontais em clipes verticais. A IA encontra os rostos, segue quem fala e mantém o enquadramento estável entre os movimentos.",
    faqs: [
      {
        question: "Como ele decide onde cortar?",
        answer:
          "Um modelo de detecção de rostos encontra cada rosto na cena e o movimento dos lábios indica quem está falando. O enquadramento centraliza em quem fala e só desliza para uma nova posição quando a pessoa sai de uma zona morta, então o quadro não treme a cada movimento de cabeça.",
      },
      {
        question: "E gravações de tela ou vídeos sem rosto?",
        answer:
          "O planejador classifica cada clipe. Gravações de tela, clipes sem um rosto para seguir e clipes em que o único rosto é uma webcam pequena no canto mantêm o quadro inteiro em vez de um corte borrado.",
      },
      {
        question: "Quais layouts posso escolher?",
        answer:
          "Tela cheia coloca quem fala em todo o quadro vertical, Streamer adiciona um fundo desfocado, Dividido empilha um segundo vídeo embaixo e Letterbox mantém o quadro inteiro sem corte. A saída pode ser 9:16, 1:1 ou 4:5.",
      },
      {
        question: "Posso ajustar o enquadramento manualmente?",
        answer:
          "Sim. Abra o clipe no editor, vá em Efeitos e depois em Reenquadrar, e edite os keyframes de câmera, ou trave a câmera em uma pessoa durante o clipe inteiro.",
      },
    ],
  },
  "video-editor": {
    related: [
      "/features/auto-clip",
      "/features/ai-captions",
      "/features/ai-reframe",
      "/features/clip-cutter",
      "/compare/crayo-vs-capcut",
      "/compare/crayo-vs-veed",
    ],
    title: "Video Editor",
    answer:
      "O editor de vídeo do Crayo é um editor de linha do tempo no navegador para clipes curtos, com faixas de vídeo, legenda, texto e áudio, além de zooms, transições, LUTs, rampas de velocidade e keyframes de câmera. Está incluído em todos os planos e foi feito para clipadores que finalizam o resultado do Auto Clip ou editam seus próprios uploads do zero.",
    description:
      "Uma linha do tempo multifaixa com legendas, zooms, transições, LUTs, rampas de velocidade, cutaways e keyframes de câmera. Sem instalar nada e sem marca d'água em nenhum plano.",
    heroPrefix: "O",
    heroHighlight: "editor de vídeo",
    heroSuffix: "com IA feito para clipes curtos",
    heroImage: "/features/video-editor/video-editor-hero.png",
    buttonText: "Abrir o editor",
    buttonLink: "/projects",
    seoTitle: "Editor de vídeo com IA para clipes curtos",
    seoDescription:
      "Edite clipes curtos no navegador com linha do tempo multifaixa, legendas com IA, zooms, transições, LUTs, rampas de velocidade e keyframes de câmera.",
    faqs: [
      {
        question: "O que tem na linha do tempo?",
        answer:
          "Faixas separadas para vídeo, legendas, texto, áudio e efeitos. Divida um clipe no cursor de reprodução, corte o início ou o fim, reordene e sobreponha cutaways acima do clipe principal enquanto o áudio de baixo abaixa automaticamente.",
      },
      {
        question: "Quais efeitos estão incluídos?",
        answer:
          "Zooms com tempo e suavização, transições como fade, deslizar, whip, punch, glitch e flashes, correção de cor com LUTs, rampas de velocidade com keyframes e um painel de Câmera e reenquadramento para mover o corte ao longo do tempo.",
      },
      {
        question: "Como as legendas funcionam?",
        answer:
          "Gere legendas com IA no clipe ou escreva manualmente, depois escolha entre 32 modelos e defina palavras por linha, posição, animação, ênfase e destaque de palavras-chave. As mudanças podem valer para uma linha ou para todas.",
      },
      {
        question: "O editor está em todos os planos?",
        answer:
          "Sim. Todos os planos do Crayo incluem o editor completo. Os planos diferem nos minutos de vídeo finalizado que você pode exportar por mês, e a prévia dos efeitos precisa de Chrome ou Edge.",
      },
    ],
  },
};
